import { useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { useGetProductsStatusQuery } from "../../services/order/orderServices";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { ToggleButton } from "primereact/togglebutton";

const ProductStatusReportTableContent = () => {
  const [showAllData, setShowAllData] = useState(false);

  const {
    data: productsStatus,
    isLoading: productsStatusLoading,
    error: productsStatusError,
    isFetching: productsStatusFetching,
    currentData,
    refetch,
  } = useGetProductsStatusQuery(showAllData ? { date: "all" } : undefined);

  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const toast = useRef(null);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
  };

  const toggleDataRange = () => {
    setShowAllData((prev) => !prev);
    refetch();
  };

  const exportToPDF = () => {
    if (!productsStatus?.data?.length) {
      toast.current.show({
        severity: "error",
        summary: "Export Failed",
        detail: "No data to export",
        life: 3000,
      });
      return;
    }

    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(16);
    doc.text("Products Delivery Status Report", 14, 10);

    // Create a formatted copy of the data with price values including $ symbol
    const formattedData = productsStatus.data.map((item) => ({
      ...item,
      price: item.price ? `$${item.price}` : "$0",
    }));

    const columns = [
      { header: "Product Name", dataKey: "product_name" },
      { header: "Price", dataKey: "price" },
      { header: "Empty", dataKey: "empty_delivery_status" },
      { header: "Total Trees Needed", dataKey: "total_trees_needed" },
      { header: "Took Trees", dataKey: "took_trees" },
      { header: "Delivered", dataKey: "delivered" },
      { header: "Tiki Bar Rental", dataKey: "tiki_bar_rental" },
      { header: "Cancelled", dataKey: "cancelled" },
    ];

    doc.autoTable({
      columns: columns,
      body: formattedData, // Use the formatted data with $ symbol
      startY: 20,
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [13, 96, 71] },
    });

    doc.save("products_delivery_status_report.pdf");

    toast.current.show({
      severity: "success",
      summary: "Export Successful",
      detail: "Data exported to PDF",
      life: 3000,
    });
  };

  const productNameTemplate = (rowData) => {
    return <div className="py-4">{rowData.product_name}</div>;
  };

  const priceTemplate = (rowData) => {
    return <div className="py-4">${rowData.price}</div>;
  };

  const statusTemplate = (rowData, field) => {
    return <div className="py-4">{rowData[field] || "0"}</div>;
  };

  const emptyDeliveryStatusTemplate = (rowData) => {
    return <div className="py-4">{rowData.empty_delivery_status}</div>;
  };
  const totalTreesnNeededTemplate = (rowData) => {
    return <div className="py-4">{rowData.total_trees_needed}</div>;
  };

  const header = () => {
    return (
      <div className="flex flex-col gap-2 bg-none">
        <div className="flex items-center justify-between">
          <div className="flex">
            <span className="mr-2 p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                className="p-inputtext-sm rounded-xl"
                value={globalFilterValue}
                onChange={onGlobalFilterChange}
                placeholder="Filter by Product Name"
              />
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">Show All Data:</span>
            <ToggleButton
              checked={showAllData}
              onChange={toggleDataRange}
              onLabel="ON"
              offLabel="OFF"
              className={`${
                showAllData
                  ? "bg-[#0D6047] text-white"
                  : "bg-[#c4e1d8] text-[#0D6047]"
              } border-none rounded-xl p-button-sm`}
            />
            <Button
              icon="pi pi-file-pdf"
              label="Export PDF"
              className="bg-[#c4e1d8] text-[#0D6047] border-none rounded-xl"
              size="small"
              onClick={exportToPDF}
            />
          </div>
        </div>
      </div>
    );
  };

  // const onShowDetails = (id, name) => {
  //   console.log("show details", id, name);
  // };

  if (productsStatusError) {
    return (
      <div className="flex items-center justify-center text-xl italic text-red-500 min-h-32 w-full h-full">
        <p>{productsStatusError?.data?.message}</p>
      </div>
    );
  }

  return (
    <div>
      <Toast ref={toast} />
      <DataTable
        value={productsStatus?.data}
        dataKey="id"
        scrollable
        scrollHeight="68vh"
        loading={
          productsStatusLoading || (productsStatusFetching && !currentData)
        }
        header={header}
        globalFilter={globalFilterValue}
        // paginator
        // rows={10}
        // rowsPerPageOptions={[
        //   5,
        //   10,
        //   25,
        //   50,
        //   ...(productsStatus?.data?.length > 50
        //     ? [productsStatus.data.length]
        //     : []),
        // ]}
      >
        <Column
          field="product_name"
          header="Product Name"
          body={productNameTemplate}
        />
        <Column field="price" header="Price" body={priceTemplate} />
        <Column
          field="empty_delivery_status"
          header="Empty"
          body={emptyDeliveryStatusTemplate}
        />
        <Column
          field="total_trees_needed"
          header="Total Trees Needed"
          body={totalTreesnNeededTemplate}
        />
        <Column
          field="took_trees"
          header="Took Trees"
          body={(rowData) => statusTemplate(rowData, "took_trees")}
        />
        <Column
          field="delivered"
          header="Delivered"
          body={(rowData) => statusTemplate(rowData, "delivered")}
        />
        <Column
          field="tiki_bar_rental"
          header="Tiki Bar Rental"
          body={(rowData) => statusTemplate(rowData, "tiki_bar_rental")}
        />
        <Column
          field="cancelled"
          header="Cancelled"
          body={(rowData) => statusTemplate(rowData, "cancelled")}
        />
      </DataTable>
    </div>
  );
};

export default ProductStatusReportTableContent;
